import React, { useState, useEffect } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

import charts from "../../../assets/charts.jpg";
import heatmap from "../../../assets/heatmap.jpg";
import reversals from "../../../assets/reversals.jpg";

// Main wrapper using grid layout for better alignment
const AuthCarouselWrapper = styled.div`
  display: grid;
  grid-template-columns: 400px 1fr;
  grid-template-rows: auto 1fr;
  grid-gap: 30px;
  max-width: 1200px;
  margin: 0 auto 5px;
  padding-top: 0; /* Remove any top padding */

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto auto;
  }
`;

// Updated FeaturesSection with reduced bottom margin
const FeaturesSection = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto 5px; /* Reduced from 80px to 20px */
  flex-wrap: wrap;
`;

// Trust statement styled to align with carousel heading
const AuthTrustStatement = styled.div`
  grid-column: 1;
  grid-row: 1;
  text-align: center;
  font-size: 18px;
  color: #a8b8c8;
  letter-spacing: 0.5px;
  font-weight: 300;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  padding-top: 0; /* Ensure no extra padding */

  @media (max-width: 1024px) {
    grid-column: 1;
    grid-row: 1;
    margin-bottom: 0;
  }
`;

// Carousel heading positioned opposite of trust statement
const CarouselHeadingStyled = styled.div`
  grid-column: 2;
  grid-row: 1;
  text-align: center;

  @media (max-width: 1024px) {
    grid-column: 1;
    grid-row: 2;
    margin-bottom: 20px;
  }
`;

// Auth component container
const AuthComponentWrapper = styled.div`
  grid-column: 1;
  grid-row: 2;
  width: 100%;

  @media (max-width: 1024px) {
    grid-column: 1;
    grid-row: 3;
    margin: 0 auto 40px;
    max-width: 400px;
  }
`;

// Carousel container opposite of auth component
const CarouselContainerStyled = styled.div`
  grid-column: 2;
  grid-row: 2;

  @media (max-width: 1024px) {
    grid-column: 1;
    grid-row: 4;
    width: 100%;
  }
`;

// Other styled components remain the same...
const BottomSection = styled.div`
  width: 100%;
  background-color: #0e1620;
  color: white;
  padding: 40px 20px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  position: relative;
  z-index: 1;
`;

const FeatureItem = styled.div`
  text-align: left;
  width: 30%;
  min-width: 300px;
  margin: 1px;
  display: flex;
  align-items: flex-start;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FeatureIconContainer = styled.div`
  margin-right: 20px;
  margin-top: 5px;
`;

const FeatureIcon = styled.svg`
  width: 50px;
  height: 50px;
  fill: #0085ff;
  transition: all 0.3s ease;

  ${FeatureItem}:hover & {
    transform: translateY(-3px);
  }
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureTitle = styled.h3`
  font-size: 20px;
  margin-bottom: 12px;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: 0.5px;
`;

const FeatureDescription = styled.p`
  color: #a8b8c8;
  font-size: 15px;
  line-height: 1.6;
`;

const TrustStatement = styled.div`
  text-align: center;
  font-size: 18px;
  color: #a8b8c8;
  margin-bottom: 80px;
  letter-spacing: 0.5px;
  font-weight: 300;
`;

const SectionDivider = styled.div`
  width: 80px;
  height: 3px;
  background: linear-gradient(90deg, #0056b3, #0085ff);
  margin: 0 auto 80px;
`;

const CarouselContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto 80px;
  padding: 20px 0;
`;

const CarouselHeading = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const CarouselTitle = styled.h2`
  font-size: 26px;
  color: #ffffff;
  font-weight: 500;
  margin-bottom: 15px;
  letter-spacing: 0.5px;
`;

const CarouselSubtitle = styled.p`
  color: #a8b8c8;
  font-size: 16px;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
  font-weight: 300;
`;

const Carousel = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 4px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  background: #131f2c;
  width: 100%;
  height: auto;
`;

const CarouselTrack = styled.div`
  display: flex;
  transition: transform 0.5s ease;
`;

const CarouselSlide = styled.div`
  min-width: 100%;
  position: relative;
  display: ${(props) => (props.active ? "block" : "none")};
`;

const SlideContent = styled.div`
  padding: 0;
`;

const SlideImage = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  text-align: center;
  background-color: #1a2736;
`;

const FeatureImage = styled.img`
  width: 65%;
  height: auto;
  display: inline-block;
  transition: transform 0.3s ease;
  object-fit: contain;
  max-height: 400px;
  padding: 30px 0;
`;

const ImageOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(10, 20, 35, 0.9);
  padding: 20px;
`;

const OverlayTitle = styled.h3`
  color: #ffffff;
  margin-bottom: 8px;
  font-size: 18px;
  font-weight: 500;
`;

const OverlayDescription = styled.p`
  color: #a8b8c8;
  font-size: 14px;
  line-height: 1.6;
`;

const CarouselNav = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: #1a2736;
`;

const CarouselButton = styled.button`
  background: none;
  border: none;
  color: #0085ff;
  font-size: 18px;
  cursor: pointer;
  padding: 8px 12px;
  transition: all 0.3s ease;

  &:hover {
    color: white;
    background: rgba(0, 133, 255, 0.2);
    border-radius: 3px;
  }
`;

const CarouselDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
`;

const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(props) => (props.active ? "#0085ff" : "#2a3a47")};
  cursor: pointer;
  transition: all 0.3s ease;
`;

const Footer = styled.div`
  text-align: center;
  padding-top: 30px;
  border-top: 1px solid #2a3a47;
  max-width: 1000px;
  margin: 0 auto;
`;

const FooterLinks = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 30px;
  margin-bottom: 20px;
`;

const FooterLink = styled.a`
  color: #a8b8c8;
  text-decoration: none;
  font-size: 14px;
  transition: color 0.3s ease;

  &:hover {
    color: #0085ff;
  }
`;

const Copyright = styled.div`
  color: #5a6a77;
  font-size: 12px;
  margin-top: 20px;
`;

const QuantumCryptoLanding = ({ authComponent }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prevSlide) => (prevSlide + 1) % 3);
    }, 7000);

    return () => clearInterval(interval);
  }, []);

  const handlePrevSlide = () => {
    setCurrentSlide((prevSlide) => (prevSlide - 1 + 3) % 3);
  };

  const handleNextSlide = () => {
    setCurrentSlide((prevSlide) => (prevSlide + 1) % 3);
  };

  const handleDotClick = (index) => {
    setCurrentSlide(index);
  };

  return (
    <BottomSection>
      {/* Features section with reduced bottom margin */}
      <FeaturesSection>
        {/* Feature items remain the same */}
        <FeatureItem>
          <FeatureIconContainer>
            <FeatureIcon viewBox="0 0 24 24">
              <path d="M12,2C6.48,2,2,6.48,2,12s4.48,10,10,10s10-4.48,10-10S17.52,2,12,2z M12,4c4.41,0,8,3.59,8,8s-3.59,8-8,8s-8-3.59-8-8S7.59,4,12,4z"></path>
              <path d="M16,14.5c0,0-2.5-1.5-4-1.5s-4,1.5-4,1.5V12c0,0,2-1.5,4-1.5s4,1.5,4,1.5V14.5z"></path>
              <path d="M12,7.5c-1.1,0-2,0.9-2,2s0.9,2,2,2s2-0.9,2-2S13.1,7.5,12,7.5z"></path>
            </FeatureIcon>
          </FeatureIconContainer>
          <FeatureContent>
            <FeatureTitle>Deep Learning Models</FeatureTitle>
            <FeatureDescription>
              Custom AI models that understand your trading patterns and
              institutional strategies, adapting to your specific investment
              profile over time.
            </FeatureDescription>
          </FeatureContent>
        </FeatureItem>

        <FeatureItem>
          <FeatureIconContainer>
            <FeatureIcon viewBox="0 0 24 24">
              <path d="M9,3L5,7H8V14H10V7H13L9,3Z"></path>
              <path d="M16,17V10H14V17H11L15,21L19,17H16Z"></path>
              <path d="M4,8H2V18C2,19.1,2.9,20,4,20H14V18H4V8Z"></path>
              <path d="M20,4H10C8.9,4,8,4.9,8,6V16C8,17.1,8.9,18,10,18H20C21.1,18,22,17.1,22,16V6C22,4.9,21.1,4,20,4Z M20,16H10V6H20V16Z"></path>
            </FeatureIcon>
          </FeatureIconContainer>
          <FeatureContent>
            <FeatureTitle>Mass Surveillance Scanning</FeatureTitle>
            <FeatureDescription>
              Comprehensive real-time monitoring of major and minor
              cryptocurrencies, capturing market movements and pattern
              formations as they develop.
            </FeatureDescription>
          </FeatureContent>
        </FeatureItem>
        <FeatureItem>
          <FeatureIconContainer>
            <FeatureIcon viewBox="0 0 24 24">
              <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4"></path>
              <path d="M12,6A3,3 0 0,0 9,9C9,11 12,15 12,15C12,15 15,11 15,9A3,3 0 0,0 12,6"></path>
              <path d="M7,10l-1.5,6l1.5,1h10l1.5-1l-1.5-6"></path>
            </FeatureIcon>
          </FeatureIconContainer>
          <FeatureContent>
            <FeatureTitle>Advanced Technical Analysis</FeatureTitle>
            <FeatureDescription>
              Identify opportunities instantly with AI pattern recognition,
              customizable pivot tables, and comprehensive technical indicators
              in an advanced charting package.
            </FeatureDescription>
          </FeatureContent>
        </FeatureItem>
      </FeaturesSection>

      {/* Grid-based layout - no standalone TrustStatement between FeaturesSection and AuthCarouselWrapper */}
      <AuthCarouselWrapper>
        {/* Trust statement (top left) */}
        <AuthTrustStatement>
          Trusted by leading financial institutions and asset managers worldwide
        </AuthTrustStatement>

        {/* Carousel heading (top right) */}
        <CarouselHeadingStyled>
          <CarouselTitle>Enterprise Trading Technology</CarouselTitle>
          <CarouselSubtitle>
            Our deep learning models understand the way you trade, finding
            opportunities instantly through mass surveillance of the
            cryptocurrency markets.
          </CarouselSubtitle>
        </CarouselHeadingStyled>

        {/* Auth component (bottom left) */}
        <AuthComponentWrapper>{authComponent}</AuthComponentWrapper>

        {/* Carousel (bottom right) */}
        <CarouselContainerStyled>
          <Carousel>
            <CarouselTrack>
              <CarouselSlide active={currentSlide === 0}>
                <SlideContent>
                  <SlideImage>
                    <FeatureImage
                      src={charts}
                      alt="Multi-Asset Analytics Dashboard"
                    />
                    <ImageOverlay>
                      <OverlayTitle>
                        AI-Powered Pattern Recognition
                      </OverlayTitle>
                      <OverlayDescription>
                        Find any type of pattern formation using our proprietary
                        deep learning algorithms that identify opportunities
                        almost every time
                      </OverlayDescription>
                    </ImageOverlay>
                  </SlideImage>
                </SlideContent>
              </CarouselSlide>
              <CarouselSlide active={currentSlide === 1}>
                <SlideContent>
                  <SlideImage>
                    <FeatureImage src={heatmap} alt="Risk Management Suite" />
                    <ImageOverlay>
                      <OverlayTitle>Real-time Surveillance System</OverlayTitle>
                      <OverlayDescription>
                        Mass scanning of major and minor cryptocurrencies to
                        capture every market movement as it happens
                      </OverlayDescription>
                    </ImageOverlay>
                  </SlideImage>
                </SlideContent>
              </CarouselSlide>
              <CarouselSlide active={currentSlide === 2}>
                <SlideContent>
                  <SlideImage>
                    <FeatureImage
                      src={reversals}
                      alt="Compliance Reporting System"
                    />
                    <ImageOverlay>
                      <OverlayTitle>
                        Customizable Trading Interface
                      </OverlayTitle>
                      <OverlayDescription>
                        Flexible pivot tables, customizable views, and advanced
                        charting with key technical indicators for institutional
                        traders
                      </OverlayDescription>
                    </ImageOverlay>
                  </SlideImage>
                </SlideContent>
              </CarouselSlide>
            </CarouselTrack>
            <CarouselNav>
              <CarouselButton onClick={handlePrevSlide}>
                &#10094;
              </CarouselButton>
              <CarouselDots>
                {[0, 1, 2].map((index) => (
                  <Dot
                    key={index}
                    active={currentSlide === index}
                    onClick={() => handleDotClick(index)}
                  />
                ))}
              </CarouselDots>
              <CarouselButton onClick={handleNextSlide}>
                &#10095;
              </CarouselButton>
            </CarouselNav>
          </Carousel>
        </CarouselContainerStyled>
      </AuthCarouselWrapper>

      {/* Footer */}
      <Footer>
        <FooterLinks>
          <FooterLink
            href="https://permutable.ai/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </FooterLink>
          <FooterLink href="mailto:enquiries@permutable.ai">
            Contact Us
          </FooterLink>
        </FooterLinks>
        <Copyright>© 2025 QuantumCrypto. All rights reserved.</Copyright>
      </Footer>
    </BottomSection>
  );
};

QuantumCryptoLanding.propTypes = {
  authComponent: PropTypes.node,
};

export default QuantumCryptoLanding;
